import { pushEvent } from '@/js/lib/analytics/data-layer';

const VERIFIED_QUERY_PARAM = 'verified';
const VERIFIED_UID_QUERY_PARAM = 'uid';
const VERIFIED_SENT_KEY = 'tcw_analytics_email_verified_sent';

export function trackGenerateLead(placement: string): void {
  pushEvent('generate_lead', {
    form_name: 'request_introduction',
    form_placement: placement,
  });
}

export function trackSignUp(sourceOfDiscovery: string): void {
  pushEvent('sign_up', {
    method: 'email',
    source_of_discovery: sourceOfDiscovery === '' ? 'not_specified' : sourceOfDiscovery,
  });
}

export function trackLogin(): void {
  pushEvent('login', { method: 'email' });
}

export function trackFileDownload(params: {
  format: string
  title: string
  documentId: number
}): void {
  pushEvent('file_download', {
    file_extension: params.format,
    file_name: params.title,
    document_id: params.documentId,
  });
}

export function trackVideoStart(params: {
  provider: string
  title: string
  url: string
}): void {
  pushEvent('video_start', {
    video_provider: params.provider,
    video_title: params.title,
    video_url: params.url,
  });
}

/**
 * Fires once after the server redirect from /account/email/verify/{id}/{hash}.
 *
 * Must run before the router starts: guards.ts redirects (/account -> dashboard, unverified ->
 * verify-email) drop the query string, so route.query is not a reliable source.
 */
export function trackEmailVerifiedOnce(): void {
  const params = new URLSearchParams(window.location.search);

  if (params.get(VERIFIED_QUERY_PARAM) !== '1') {
    return;
  }

  // Scoped to the verified account: one device serves several of them, and a single global key
  // would suppress the event for everyone after the first verification.
  const sentKey = `${VERIFIED_SENT_KEY}:${params.get(VERIFIED_UID_QUERY_PARAM) ?? 'unknown'}`;

  // Strip the flags first so a reload cannot replay the event even when the guard below is
  // unavailable (private mode).
  params.delete(VERIFIED_QUERY_PARAM);
  params.delete(VERIFIED_UID_QUERY_PARAM);

  const query = params.toString();

  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query === '' ? '' : `?${query}`}${window.location.hash}`,
  );

  let alreadySent = false;

  try {
    alreadySent = localStorage.getItem(sentKey) === '1';
    localStorage.setItem(sentKey, '1');
  }
  catch {
    // Storage disabled — replaceState above still guards against reloads.
  }

  if (alreadySent) {
    return;
  }

  pushEvent('email_verified', { method: 'email_link' });
}
